import type { CSSProperties, FC, ReactNode } from 'react';
import { Popover, Typography } from 'antd';
import type { PopoverProps } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';

export interface EnrichedTooltipContentProps {
    description: ReactNode;
    interactiveVisual?: boolean;
    label: ReactNode;
    learnMoreHref?: string;
    learnMoreLinkText?: string;
    learnMoreTopic: ReactNode;
    visual: ReactNode;
    width?: CSSProperties['width'];
}

export interface EnrichedTooltipProps extends EnrichedTooltipContentProps {
    ariaLabel?: string;
    children: ReactNode;
    placement?: PopoverProps['placement'];
}

export const EnrichedTooltipContent: FC<EnrichedTooltipContentProps> = ({
    description,
    interactiveVisual = false,
    label,
    learnMoreHref,
    learnMoreLinkText,
    learnMoreTopic,
    visual,
    width = '20rem',
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <div aria-label={typeof label === 'string' ? label : undefined} role='dialog' style={{ width }}>
            <div
                aria-hidden={interactiveVisual ? undefined : true}
                style={{
                    alignItems: 'center',
                    backgroundColor: token.colorBgBase,
                    borderBottom: `1px solid ${token.colorBorder}`,
                    display: 'flex',
                    justifyContent: 'center',
                    minHeight: '8rem',
                    overflow: 'hidden',
                    padding: '0.75rem',
                    pointerEvents: interactiveVisual ? 'auto' : 'none',
                }}
            >
                {visual}
            </div>
            <div style={{ padding: '0.75rem' }}>
                <Typography.Text strong style={{ color: token.colorText, display: 'block', marginBottom: '0.25rem' }}>
                    {label}
                </Typography.Text>
                <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: 0 }}>
                    {description}
                </Typography.Paragraph>
            </div>
            <LearnMoreFooter
                href={learnMoreHref}
                linkText={learnMoreLinkText}
                topic={learnMoreTopic}
                variant='dropdown'
            />
        </div>
    );
};

/**
 * A rich, interactive companion to SmallTooltip for concepts that benefit
 * from a visual example, supporting context, and a documentation path.
 */
const EnrichedTooltip: FC<EnrichedTooltipProps> = ({
    ariaLabel,
    children,
    description,
    interactiveVisual,
    label,
    learnMoreHref,
    learnMoreLinkText,
    learnMoreTopic,
    placement = 'right',
    visual,
    width,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Popover
            arrow={false}
            autoAdjustOverflow
            color={token.colorBgContainer}
            content={
                <EnrichedTooltipContent
                    description={description}
                    interactiveVisual={interactiveVisual}
                    label={label}
                    learnMoreHref={learnMoreHref}
                    learnMoreLinkText={learnMoreLinkText}
                    learnMoreTopic={learnMoreTopic}
                    visual={visual}
                    width={width}
                />
            }
            destroyOnHidden
            mouseEnterDelay={0.35}
            placement={placement}
            styles={{
                body: {
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    boxShadow: token.boxShadowSecondary,
                    overflow: 'hidden',
                    padding: 0,
                },
                root: { maxWidth: 'none' },
            }}
            trigger={['hover', 'focus']}
        >
            <span
                aria-haspopup='dialog'
                aria-label={ariaLabel ?? (typeof label === 'string' ? `Learn more about ${label}` : 'Learn more')}
                role='button'
                tabIndex={0}
                style={{ cursor: 'help', display: 'inline-flex', verticalAlign: '-0.125rem' }}
            >
                {children}
            </span>
        </Popover>
    );
};

export default EnrichedTooltip;
